import type { ElowenUiRuntime } from 'elowen-plugin-ui-kit';

/** Elowen's Monaco themes, built from the ACTIVE SKIN's tokens.
 *
 *  Monaco is the one renderer in the app that cannot read a CSS custom property: it takes a flat object of
 *  literal colours at registration. That is not a licence for a second palette. This module therefore reads
 *  the design tokens off the document at the moment the editor mounts (`getComputedStyle`), so the editor is
 *  painted by the same `--color-*` values as the card it stands in — a repaint reaches it instead of leaving
 *  one frozen surface behind, and every value is stated once, in `tokens.css`, where the rest of the app
 *  reads it.
 *
 *  Two theme NAMES stay, because Monaco picks a base (`vs`/`vs-dark`) and a component asks for one by name:
 *  `elowen-oled` for a dark document and `elowen-paper` for a light one, chosen by the resolved
 *  `color-scheme` — the one signal a skin already controls, so nothing here needs to know skin names. */
type Monaco = Parameters<ElowenUiRuntime['utils']['defineEditorThemes']>[0];

/** Where a colour comes from. A parameter only so the table can be asserted without a DOM (the contract
 *  test supplies the tokens it checks); the app always takes the default, which reads the live cascade. */
export type TokenReader = (token: string) => string;

const documentTokens: TokenReader = (token) => typeof document === 'undefined'
  ? ''
  : getComputedStyle(document.documentElement).getPropertyValue(token).trim();

const RGB = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?\s*\)$/i;

/** A resolved token as the `#rrggbb`/`#rrggbbaa` string Monaco wants. A full hex passes through; the CSS short
 *  forms (`#rgb`, `#rgba`) are spelled out, because Monaco's token theme refuses them ("Illegal value for
 *  token color") and the editor then fails to mount. A token that resolves to NOTHING throws: Monaco would
 *  silently fall back to its own defaults, which looks like a skin that did not apply and gives nothing to
 *  grep for. */
function monacoColor(token: string, value: string): string {
  if (/^#(?:[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)) return value;
  if (/^#[0-9a-f]{3,4}$/i.test(value)) return `#${[...value.slice(1)].map((digit) => digit + digit).join('')}`;
  const channels = RGB.exec(value);
  if (!channels) throw new Error(`${token} does not resolve to a colour (got "${value}") — is the skin loaded?`);
  const [, r, g, b, a] = channels;
  const byte = (channel: string) => Math.round(Number(channel)).toString(16).padStart(2, '0');
  return `#${byte(r!)}${byte(g!)}${byte(b!)}${a === undefined ? '' : byte(String(Number(a) * 255))}`;
}

/** A token at a stated alpha, for the translucent selections and diff washes Monaco expects. */
function tint(token: string, value: string, alpha: number): string {
  return `${monacoColor(token, value).slice(0, 7)}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
}

/** The theme body. Every entry maps a Monaco slot onto the token that already means that thing in the app:
 *  the canvas is `--color-background`, a line number is the subtle text step, a selection is the brand ember
 *  at 30%, a diff row is the app's own add/delete colour. Nothing here invents a shade. */
function theme(base: 'vs' | 'vs-dark', read: TokenReader) {
  const token = (name: string) => monacoColor(name, read(name));
  /** A syntax rule's `foreground` is a bare hex WITHOUT the `#`. */
  const ink = (name: string) => token(name).slice(1, 7);
  return {
    base,
    inherit: true,
    rules: [
      { token: '', foreground: ink('--color-code-plain') },
      { token: 'comment', foreground: ink('--color-code-comment'), fontStyle: 'italic' },
      { token: 'string', foreground: ink('--color-code-string') },
      { token: 'number', foreground: ink('--color-code-number') },
      { token: 'keyword', foreground: ink('--color-code-keyword') },
      { token: 'type', foreground: ink('--color-code-type') },
      { token: 'delimiter', foreground: ink('--color-code-punct') },
      { token: 'tag', foreground: ink('--color-code-keyword') },
    ],
    colors: {
      'editor.background': token('--color-background'),
      'editor.foreground': token('--color-foreground'),
      'editorLineNumber.foreground': token('--color-subtle-foreground'),
      'editorLineNumber.activeForeground': token('--color-primary-hot'),
      // The line the caret rests on is a hairline, not a fill: a wash would separate one line from the block
      // it belongs to, and the app's own code rows mark the line by its rule.
      // Monaco parses theme colours as hex only; the word `transparent` would read as its error red.
      'editor.lineHighlightBackground': '#00000000',
      'editor.lineHighlightBorder': token('--color-border'),
      'editor.selectionBackground': tint('--color-primary', read('--color-primary'), 0.3),
      'editor.inactiveSelectionBackground': tint('--color-primary', read('--color-primary'), 0.16),
      'editor.selectionHighlightBackground': tint('--color-primary-hot', read('--color-primary-hot'), 0.14),
      'editorCursor.foreground': token('--color-primary-hot'),
      'editor.findMatchBackground': tint('--color-primary', read('--color-primary'), 0.32),
      'editor.findMatchHighlightBackground': tint('--color-primary-hot', read('--color-primary-hot'), 0.16),
      'editorGutter.background': token('--color-background'),
      'editorWidget.background': token('--color-popover'),
      'editorWidget.border': token('--color-border'),
      'input.background': token('--color-control'),
      'dropdown.background': token('--color-popover'),
      'editorIndentGuide.background1': token('--color-border'),
      'minimap.background': token('--color-background'),
      'diffEditor.insertedTextBackground': tint('--color-success', read('--color-success'), 0.13),
      'diffEditor.removedTextBackground': tint('--color-destructive', read('--color-destructive'), 0.13),
      'diffEditor.insertedLineBackground': tint('--color-success', read('--color-success'), 0.08),
      'diffEditor.removedLineBackground': tint('--color-destructive', read('--color-destructive'), 0.08),
    },
  };
}

/** Both theme payloads, as `[name, theme]` pairs. Exported for `tests/contract/editorOledTheme.test.ts`, which
 *  supplies its own tokens so the table can be asserted without a DOM; the runtime entry point is
 *  `defineEditorThemes` below, whose signature is the published UI API's and takes the monaco namespace
 *  alone. */
export function editorThemeDefinitions(read: TokenReader = documentTokens): [string, ReturnType<typeof theme>][] {
  return [['elowen-oled', theme('vs-dark', read)], ['elowen-paper', theme('vs', read)]];
}

export function defineEditorThemes(monaco: Monaco) {
  for (const [name, definition] of editorThemeDefinitions()) monaco.editor.defineTheme(name, definition);
}

/** The Monaco theme matching the app's current design: paper when the active skin resolved the document to a
 *  light color-scheme, the dark theme otherwise (including SSR, where the dark default matches the built-in
 *  design). */
export function editorTheme(): 'elowen-oled' | 'elowen-paper' {
  if (typeof document === 'undefined') return 'elowen-oled';
  return getComputedStyle(document.documentElement).colorScheme === 'light' ? 'elowen-paper' : 'elowen-oled';
}
